/**
 * WHICH SOLUTION EACH ANALYTICS METRIC IS EVIDENCE FOR (VERA-260).
 *
 * Every figure the Analytics page can show is classified against the marketing site's
 * solution areas, so a brand can filter the component library down to "show me what
 * proves brand protection" and can see, on each row, what a metric is for before adding
 * it. The classification is a PRODUCT TAXONOMY — it is authored, and it is the kind of
 * thing that gets corrected — so it lives in ONE readable table rather than scattered
 * across the twenty-seven card declarations in two files.
 *
 * ## The vocabulary is the marketing solutions MINUS one
 *
 * It is deliberately NOT `MetricSolution` itself:
 *
 *   EXCLUDED  `MetricSolution.AiAnalytics`. It applies to every figure on this page —
 *             the page IS the analytics product — so as a filter option it would return
 *             the whole list, and as a badge it would appear on all twenty-seven rows.
 *             A category that fits everything categorises nothing. (`AnalyticsCustomize`
 *             reaches the identical conclusion one axis over, where `metric` is excluded
 *             from the display filter because every row is capable of it.)
 *
 * All five members take their LABELS from `METRIC_SOLUTION_LABELS` rather than restating
 * them, because those strings are the marketing site's own headings and a second copy
 * here would drift from a published page with nothing comparing them (§44).
 *
 * ## ⚠️ `core` WAS A SIXTH MEMBER AND IS GONE (VERA-260, second pass)
 *
 * It named the PRIMITIVES other metrics are computed from — scans, certificates, the two
 * transfer counts, resales — on the reasoning that "scans feed brand protection AND
 * consumer engagement AND traceability equally, so picking one would be arbitrary."
 *
 * That reasoning was right about the DATA and wrong about the VOCABULARY. Everything it
 * says is a statement that those metrics are evidence for no solution IN PARTICULAR — so
 * `core` was never a peer of "Brand protection", it was the ABSENCE of one wearing a
 * peer's chrome. Rendered as a sixth badge beside five real ones, and offered as a sixth
 * filter option, it asserted a kind of thing it is not.
 *
 * The five primitives now carry an **explicitly empty array**, and the difference between
 * that and a missing key is the whole mechanism (see `ANALYTICS_METRIC_SOLUTIONS`): `[]`
 * is a decision, an absent key is a compile error. A row with `[]` renders NO badge row at
 * all — not an empty row and not a placeholder.
 *
 * ⚠️ CONSEQUENCE, stated rather than discovered: there is no longer any way to FILTER the
 * library down to just the primitives. If that turns out to be a real loss the answer is a
 * separate "Uncategorised"-style option in the filter — a control that means "rows with no
 * solution", which is what the reader would actually be asking for — NOT a resurrected
 * `core` pretending to be a solution.
 *
 * ## The set is CLOSED, for the same reason the glossary's `tags` are
 *
 * `context/scripts/publish-glossary.mjs` validates its tag vocabulary against a closed
 * set and states the reason exactly: "a typo in an open vocabulary produces a tag with
 * one member and no error, and the entry silently drops out of the index it was meant to
 * join." Identical here — a mistyped category would produce a filter option matching one
 * row, and both the option and the badge would look deliberate.
 *
 * The mechanism is stronger than the glossary's, because this vocabulary lives in
 * TypeScript rather than in JSON: `AnalyticsSolutionCategory` is a union, so a typo is a
 * compile error at the point it is written, and `ANALYTICS_SOLUTION_LABELS` is TOTAL over
 * it, so a category added without a label is a compile error too.
 *
 * ## Coverage is discharged by the COMPILER, not by a convention
 *
 * `ANALYTICS_METRIC_SOLUTIONS` is `Record<AnalyticsMetricId, …>` — total, exactly as
 * `SERIES_COUNT_FIELDS`, `VALUE_UNIT` and `TABLE_RANKABLE` are in `analyticsDisplay`.
 * What closes the loop is that `AnalyticsMetricId` is also the type of `ROICardDef['id']`
 * and of `OverviewStatBox['key']`, which makes adding an uncategorised metric impossible
 * in two steps rather than merely discouraged:
 *
 *   1. Declare a new ROI card or KPI tile -> its id is not in `AnalyticsMetricId`, so the
 *      card's own declaration fails to compile.
 *   2. Add the id to the union -> `ANALYTICS_METRIC_SOLUTIONS` is now missing a key, so
 *      THIS file fails to compile until the metric is classified.
 *
 * A lookup table keyed on `string` would give the same runtime answer and none of that:
 * it would go stale silently in both directions, which is the failure this page has
 * already been bitten by (see `AnalyticsCustomize` on the three disjoint id spaces).
 *
 * ## What is deliberately NOT here
 *
 * ⚠️ THE ROI HERO'S THREE FIGURES — Return on Vera, Value delivered, Spend with Vera —
 * are not classified and must not be. They are not library components: they cannot be
 * added, removed, filtered or badged, because the hero is fixed page chrome rather than
 * a card on the canvas. More to the point, "Value delivered" is by construction the sum
 * ACROSS solutions and "Spend with Vera" is a billing figure that is evidence for none of
 * them — which is why `METRIC_REGISTRY` already gives `vera_spend` an EMPTY `solutions`
 * array. Filing either under a solution would be a claim, not a classification.
 *
 * ⚠️ THE DISPLAY VARIANTS (`scans_count::chart`, composed cards) are not keys here. A
 * variant is a DRAWING of a metric already in this table, and it inherits that metric's
 * classification; the Layout panel filters variant ids out of its list before this table
 * is consulted (`metricRows`). `analyticsMetricSolutions` therefore takes the base id.
 */
import {
  LeafIcon,
  RecycleIcon,
  RouteIcon,
  ShieldCheckIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react'

import { METRIC_SOLUTION_LABELS, MetricSolution } from '@repo/types'

// The id grammar, so a VARIANT id resolves to the metric it is a variant of — see
// `analyticsMetricSolutions`. `analyticsGrid` is a leaf module (it imports only types),
// so this closes no cycle.
import { parseAnalyticsVariantCardId } from './analyticsGrid'

/**
 * The vocabulary, in the order the filter offers it and the badges render it.
 *
 * Authored rather than derived from `MetricSolution` so it does not reshuffle when the
 * enum gains a member, and so the AiAnalytics omission above is a visible absence from a
 * list rather than a subtraction someone has to reconstruct.
 */
export const ANALYTICS_SOLUTION_ORDER = [
  MetricSolution.BrandProtection,
  MetricSolution.ResaleCircularity,
  MetricSolution.ConsumerEngagement,
  MetricSolution.TraceabilityLogistics,
  MetricSolution.SustainabilityCompliance,
] as const

export type AnalyticsSolutionCategory = (typeof ANALYTICS_SOLUTION_ORDER)[number]

/**
 * ⚠️ TOTAL over the vocabulary, so a category added above without a label is a compile
 * error rather than a badge that renders `undefined`.
 *
 * The five solution labels are READ from `METRIC_SOLUTION_LABELS`, never retyped — they
 * are the marketing site's headings, and the whole reason that constant exists is that a
 * second copy of a published string drifts silently.
 */
export const ANALYTICS_SOLUTION_LABELS: Record<AnalyticsSolutionCategory, string> = {
  [MetricSolution.BrandProtection]: METRIC_SOLUTION_LABELS[MetricSolution.BrandProtection],
  [MetricSolution.ResaleCircularity]: METRIC_SOLUTION_LABELS[MetricSolution.ResaleCircularity],
  [MetricSolution.ConsumerEngagement]: METRIC_SOLUTION_LABELS[MetricSolution.ConsumerEngagement],
  [MetricSolution.TraceabilityLogistics]:
    METRIC_SOLUTION_LABELS[MetricSolution.TraceabilityLogistics],
  [MetricSolution.SustainabilityCompliance]:
    METRIC_SOLUTION_LABELS[MetricSolution.SustainabilityCompliance],
}

/**
 * ── ONE GLYPH PER SOLUTION, and this table is its only home (§36) ─────────────────────
 *
 * ⚠️ TOTAL over the vocabulary, so a category added above without a glyph is a compile
 * error rather than a badge that renders nothing where its siblings have a mark.
 *
 * ## Why the badge gets an icon at all, having shipped without one
 *
 * `solutionOptions` used to carry a note explaining the absence: "there is no established
 * glyph for a solution, and inventing six would be six new symbols to learn for no
 * scanning benefit — the labels are already distinct words." The first clause is still
 * true and the conclusion no longer follows, for a reason that changed underneath it: the
 * badges are now BORDERLESS TINTED TAGS sitting directly above a row of OUTLINED CONTROLS
 * (`Display as`), and the chips in that row each carry a leading glyph. A glyph-less tag
 * beside a glyphed chip reads as the tag being unfinished rather than as it being a
 * different kind of thing. The mark is now part of what says "metadata, from a fixed
 * vocabulary" — the same job `AnalyticsCategoryGlyph` does one row down.
 *
 * The cost is real and was measured, not waved past: an icon plus its 4px gap adds ~17px
 * to every badge, which is the single largest input to how many library rows wrap their
 * badges to a second line. See `SOLUTION_BADGE_CAP` for the arithmetic and for the
 * label-only fallback if that trade is ever judged the wrong way round.
 *
 * ## The glyphs, and what each depicts rather than decorates
 *
 * Each names the EVIDENCE its solution is about, so the mark carries identity beside the
 * word rather than ornamenting it — the same test `CATEGORY_ICON` applies to Metric /
 * Chart / Map.
 *
 *   Brand protection            a shield with a check — something verified and held off
 *   Resale & circularity        the recycling loop — an item going round again
 *   Consumer engagement         people — who is scanning
 *   Traceability & logistics    a route — a path through custody, NOT a truck (which
 *                               would name shipping specifically, one mode of a solution
 *                               that is about knowing where a thing is by any means)
 *   Sustainability & compliance a leaf. Names the sustainability half only; there is no
 *                               single mark for "durability AND regulatory reporting",
 *                               and a document/clipboard would name the compliance half
 *                               just as partially while being far less recognisable.
 *
 * ⚠️ SAME GLYPH EVERYWHERE THE SOLUTION APPEARS — the badge and the filter's option row
 * both read this table. A second `solution -> glyph` map is exactly how a category ends up
 * with a shield on the badge and nothing in the menu (`AnalyticsCategoryLabel` records
 * having been bitten by precisely that when its own map held pre-sized elements).
 */
export const ANALYTICS_SOLUTION_ICONS: Record<AnalyticsSolutionCategory, LucideIcon> = {
  [MetricSolution.BrandProtection]: ShieldCheckIcon,
  [MetricSolution.ResaleCircularity]: RecycleIcon,
  [MetricSolution.ConsumerEngagement]: UsersIcon,
  [MetricSolution.TraceabilityLogistics]: RouteIcon,
  [MetricSolution.SustainabilityCompliance]: LeafIcon,
}

/**
 * One line per category, for the filter option's tooltip and the badge's.
 *
 * A solution NAME is a marketing heading, and a brand reading "Traceability & Logistics"
 * on a row next to "Product lifespan" has no way to tell whether it describes the metric
 * or the tab it came from. The sentence says which.
 */
export const ANALYTICS_SOLUTION_BLURBS: Record<AnalyticsSolutionCategory, string> = {
  [MetricSolution.BrandProtection]:
    'Evidence that counterfeit, tampered or stolen goods are being caught.',
  [MetricSolution.ResaleCircularity]:
    'Evidence about items changing hands after their first sale.',
  [MetricSolution.ConsumerEngagement]:
    'Evidence about who is scanning, how often, and where they are.',
  [MetricSolution.TraceabilityLogistics]:
    'Evidence about where items are and how they move through custody.',
  [MetricSolution.SustainabilityCompliance]:
    'Evidence for durability, longevity and compliance reporting.',
}

/**
 * Every metric the component library can place, as a union.
 *
 * ⚠️ THIS UNION IS THE TYPE OF `ROICardDef['id']` AND `OverviewStatBox['key']`. That is
 * what makes the coverage guarantee in the header real rather than aspirational — see
 * those two declarations. Widening either back to `string` silently reopens the gap.
 *
 * ⚠️ AN ID IS A PERSISTED KEY, not display copy. Every one of these appears inside saved
 * views' `cardIds` and in each layout item's `i`, so renaming one does not migrate a
 * stored view — it silently drops the card from every view that already referenced it.
 * Several are deliberately STALE relative to their card's title for exactly this reason
 * (`mkt_geo_reach` is titled "Countries reached"; `log_avg_transfer_time` reports a
 * median). Do not tidy them.
 */
export type AnalyticsMetricId =
  // The four KPI tiles. A tile's id IS the totals field it displays.
  | 'scans_count'
  | 'certificates_count'
  | 'completed_transfers_count'
  | 'in_progress_transfers_count'
  // Marketing ROI cards.
  | 'mkt_repeat_scan_rate'
  | 'mkt_geo_reach'
  | 'mkt_invalid_scans'
  | 'mkt_invalid_scan_rate'
  | 'mkt_leads_captured'
  | 'mkt_time_to_first_scan'
  // Legal ROI cards.
  | 'legal_authenticity_rate'
  | 'legal_counterfeit_deflections'
  | 'legal_transfer_completion_rate'
  | 'legal_certificate_coverage'
  | 'return_fraud_value_recovered'
  | 'legal_stolen_reports'
  | 'legal_lost_reports'
  // Logistics ROI cards.
  | 'log_avg_transfer_time'
  | 'log_time_to_first_resale'
  | 'log_product_lifespan'
  | 'log_in_transit_rate'
  | 'log_scan_location_accuracy'
  | 'log_fulfillment_visibility'
  | 'log_items_per_owner'
  | 'log_resales'
  | 'log_avg_resale_price'
  | 'log_resales_per_item'
  /*
    ⚠️ NO LONGER A CARD — IT IS AN ALIAS, AND THE MEMBER STAYS ANYWAY (VERA-260).

    `scans_by_country` WAS a standalone panel card with its own in-card metric picker,
    which is why it needed a row here at all: it was reached by neither of the two
    declarations that carry `AnalyticsMetricId`, so it survived into `metricRows` as a real
    library row and shipped for one build with a bare title and no badge — exactly what an
    uncategorised metric looks like, i.e. indistinguishable from one with no solutions.

    The render pass retired it. Grouping is an axis now, so the choropleth is the map
    grouped by country and the id forward-maps to `scans_count::map@country`
    (`migrateAnalyticsCardId`). The library row that replaced it is a PRESET
    (`analyticsIsLibraryPresetCardId`), and its badges come from `scans_count` — because
    `analyticsMetricSolutions` resolves a variant id to its base metric, which is the fix
    that keeps the preset row from repeating the bare-badge bug above.

    ⚠️ SO WHY KEEP THE MEMBER? Because the ID is still real: `SCANS_BY_COUNTRY_CARD_ID`
    carries `satisfies AnalyticsMetricId` at its declaration in `analyticsGrid`, and that
    constant survives to be forward-mapped and to be recognised by `analyticsExport`.
    Deleting this member breaks that `satisfies`. Its row below is therefore reachable by
    the TYPE and not by any rendered badge — dead weight that costs one line and whose
    removal would cost a compile error, so it stays until the alias itself can go.

    ⚠️ ONE STALE FIGURE THIS INVALIDATES: `SOLUTION_BADGE_CAP`'s note calls this row "the
    one metric carrying three categories", i.e. the only row that makes `+N` reachable.
    That row now shows `scans_count`'s categories instead, so `+N` may currently be
    unreachable again. The cap stays at 2 regardless — the argument for it is about what
    happens as the taxonomy is corrected, not about today's fan-out.
  */
  | 'scans_by_country'

const Sol = MetricSolution

/**
 * ── THE CLASSIFICATION ───────────────────────────────────────────────────────────────
 *
 * ⚠️ TOTAL over `AnalyticsMetricId`. A metric added to that union without a row here is a
 * compile error, which is the whole mechanism — see the header.
 *
 * ## How these were decided, so a correction can argue with the reasoning
 *
 * Where a card has a counterpart in `METRIC_REGISTRY`, ITS `solutions` are used. That
 * registry was authored for this exact question (its own header: "what does the marketing
 * site's <solution> page prove?"), so re-deciding here would produce a second opinion on
 * a settled one. Eleven rows below follow it. `AiAnalytics` is dropped in the copy, per
 * the header.
 *
 * ⚠️ FOLLOWING THE REGISTRY IS NOT MATCHING ON TITLE, and the distinction is the one
 * `AnalyticsCustomize` and `roiCardDefinitions` both warn about at length. Those warnings
 * are about asserting that a card IS a registry metric — which would make a COUNT card
 * (`legal_counterfeit_deflections`, incidents) claim the identity of a CURRENCY metric
 * (`counterfeit_deflection_value`) that feeds the ROI hero, and with it that metric's
 * contribution. Nothing here asserts identity: `metricId` is untouched, no figure moves,
 * and this table only records what SUBJECT AREA a figure is evidence for. Two different
 * measurements of counterfeiting are both evidence for brand protection, and saying so
 * costs nothing that a wrong `metricId` would cost.
 *
 * The remaining rows are reasoned from what the figure is evidence FOR — never from which
 * ROI TAB it happens to sit on. The tabs are a ROLE axis (`MetricRole`), and the two
 * genuinely cross-product: `legal_transfer_completion_rate` sits on the Legal tab and is
 * evidence for Resale & circularity, which is the registry's own answer for it.
 */
export const ANALYTICS_METRIC_SOLUTIONS: Record<
  AnalyticsMetricId,
  readonly AnalyticsSolutionCategory[]
> = {
  /* ── THE PRIMITIVES: DELIBERATELY EMPTY ───────────────────────────────────────────
     Each of these is a raw count that other rows in this table are computed FROM, which
     is the test — not "we could not decide". Scans is the base of the invalid-scan pair,
     the repeat rate, time-to-first-scan, countries reached and fulfillment visibility;
     certificates of coverage and items-per-owner; the two transfer counts of the
     completion rate, the completion time and the in-transit rate; resales of the average
     price and resales-per-item.

     A primitive feeds brand protection AND consumer engagement AND traceability equally,
     so it is evidence for no solution in particular — which is a fact about the metric,
     and `[]` is how this table says it. These rows render NO badge row at all.

     ⚠️ `[]` IS THE DECISION; A MISSING KEY IS THE BUG. They are not interchangeable and
     the distinction is the reason this stayed a total `Record` rather than a partial one.
     `scans_by_country` shipped for one build with no key here at all and rendered exactly
     like a metric that carries no solutions — indistinguishable, with nothing to say which
     it was. Requiring the key keeps the compile error for the forgotten case while still
     admitting the deliberate one.

     ⚠️ These used to read `[ANALYTICS_SOLUTION_CORE]`. See the header for why that
     category is gone and why "uncategorised" is a filter option if anyone wants one back.

     ⚠️ BOTH TRANSFER TILES ARE EMPTY, not just the completed one. They split ONE cohort of
     started transfers by where it got to (see `OVERVIEW_STAT_BOXES`), so they are one
     primitive counted two ways; classifying only half of it would be arbitrary. */
  scans_count: [],
  certificates_count: [],
  completed_transfers_count: [],
  in_progress_transfers_count: [],
  log_resales: [],

  /* ── Brand protection ─────────────────────────────────────────────────────────────
     What a rejected scan, a failed verification or a stolen report is evidence of. */
  // Registry: `counterfeit_deflection_value` -> BrandProtection.
  mkt_invalid_scans: [Sol.BrandProtection],
  mkt_invalid_scan_rate: [Sol.BrandProtection],
  // Registry: `authenticity_verification_rate` -> BrandProtection.
  legal_authenticity_rate: [Sol.BrandProtection],
  legal_counterfeit_deflections: [Sol.BrandProtection],
  // Registry: `certificate_coverage` -> BrandProtection + SustainabilityCompliance. The
  // second is not incidental — coverage is the denominator every DPP-style compliance
  // claim rests on, so an uncertified catalogue is a compliance gap as much as a
  // protection one.
  legal_certificate_coverage: [Sol.BrandProtection, Sol.SustainabilityCompliance],
  // Registry: `return_fraud_value_recovered` -> BrandProtection + TraceabilityLogistics.
  // This card IS that registry metric (it carries its `metricId`), so this row is the
  // registry's own answer rather than an interpretation of it.
  return_fraud_value_recovered: [Sol.BrandProtection, Sol.TraceabilityLogistics],
  /*
    Registry: `stolen_recovery_value` -> BrandProtection + TraceabilityLogistics.

    ⚠️ `legal_lost_reports` TAKES THE SAME PAIR AS STOLEN, AND THAT IS THE ROW MOST WORTH
    ARGUING WITH. Lost and stolen are rendered as a pair and counted the same way (a dated
    report event), but they are not the same claim: stolen is an adversarial event and
    plainly brand protection, whereas lost is an owner mislaying their own property, which
    is closer to pure custody. Kept as a pair because a lost item that later resurfaces is
    detected by the same mechanism a stolen one is, so the two feed one workflow — but
    Traceability-only for `legal_lost_reports` is a defensible correction.
  */
  legal_stolen_reports: [Sol.BrandProtection, Sol.TraceabilityLogistics],
  legal_lost_reports: [Sol.BrandProtection, Sol.TraceabilityLogistics],

  /* ── Consumer engagement ──────────────────────────────────────────────────────────
     Who is scanning, how often, how soon, and how much they hold. */
  // Registry: `repeat_scan_rate` -> ConsumerEngagement.
  mkt_repeat_scan_rate: [Sol.ConsumerEngagement],
  // Registry: `geographic_reach` -> ConsumerEngagement + TraceabilityLogistics.
  mkt_geo_reach: [Sol.ConsumerEngagement, Sol.TraceabilityLogistics],
  mkt_leads_captured: [Sol.ConsumerEngagement],
  /*
    Engagement, not logistics. The gap from certificate creation to first scan reads as a
    shipping-and-shelf duration and could be argued into Traceability — but Vera does not
    observe the item between those two moments, so the figure is only ever evidence about
    when a HUMAN first engaged, which is the engagement question.
  */
  mkt_time_to_first_scan: [Sol.ConsumerEngagement],
  /*
    Customer depth, not catalogue size — the card's own description makes the point ("a
    collector holding six identical watches holds six items"). That is a statement about a
    relationship, so Consumer engagement rather than Resale.
  */
  log_items_per_owner: [Sol.ConsumerEngagement],

  /* ── Resale & circularity ─────────────────────────────────────────────────────────
     Items changing hands after the brand's first handoff. */
  // Registry: `transfer_completion_rate` -> ResaleCircularity.
  legal_transfer_completion_rate: [Sol.ResaleCircularity],
  /*
    The rate's own registry answer is Resale-only; the TIME version adds Traceability
    because a duration is a custody-gap measurement in a way a success rate is not — it
    is the window during which an item's owner of record is stale.
  */
  log_avg_transfer_time: [Sol.ResaleCircularity, Sol.TraceabilityLogistics],
  log_time_to_first_resale: [Sol.ResaleCircularity],
  log_avg_resale_price: [Sol.ResaleCircularity],
  log_resales_per_item: [Sol.ResaleCircularity],

  /* ── Traceability & logistics ─────────────────────────────────────────────────────
     Where items are and how they move. */
  // Registry: `in_transit_rate` -> TraceabilityLogistics.
  log_in_transit_rate: [Sol.TraceabilityLogistics],
  // Registry: `scan_location_accuracy` -> TraceabilityLogistics + AiAnalytics; the second
  // is dropped per the header.
  log_scan_location_accuracy: [Sol.TraceabilityLogistics],
  log_fulfillment_visibility: [Sol.TraceabilityLogistics],

  /* ── Sustainability & compliance ──────────────────────────────────────────────────
     ⚠️ The ONLY row whose primary category is Sustainability, which is worth noticing
     rather than smoothing over: this page can barely evidence that solution today. The
     card's own description is candid that it measures how long items are still being
     SEEN, not how long they last, and that anything still in circulation keeps adding to
     it — but "still in use after N days" is precisely the circularity claim, which is why
     it carries Resale & circularity alongside. */
  log_product_lifespan: [Sol.SustainabilityCompliance, Sol.ResaleCircularity],

  /* ── The choropleth: the only THREE-category row, and it earns all three ──────────────
     Every other row is one metric, so it answers one question. This card is a metric
     PICKER over three columns of one pre-aggregated response, and the three are evidence
     for different things: invalid scan rate by country is where counterfeiting concentrates
     (Brand protection), repeat scan rate by country is engagement depth (Consumer
     engagement), and scan volume by country is reach and custody geography (Traceability &
     logistics — the same pair `mkt_geo_reach` carries, for the same reason).

     Not inflated to exercise the badge overflow: a card whose whole purpose is switching
     between three differently-purposed measures genuinely serves all three, and giving it
     one category would hide two of its three uses behind a filter. It is, however, what
     makes `+N` reachable today — see `SOLUTION_BADGE_CAP`. */
  scans_by_country: [
    Sol.BrandProtection,
    Sol.ConsumerEngagement,
    Sol.TraceabilityLogistics,
  ],
}

/**
 * The categories a library row is evidence for.
 *
 * Takes a plain `string` rather than `AnalyticsMetricId` because the caller holds an
 * `AnalyticsCardItem`, whose `id` is widened to cover display-variant and composed ids
 * too. Those never reach here — the Layout panel filters them out before it lists a row
 * — but typing the parameter narrowly would force a cast at the one call site, which
 * buys nothing and hides the fallback below.
 *
 * An unknown id yields `[]`, which by the header's coverage argument is unreachable for
 * any card in the library. It is a fallback rather than a throw for the reason
 * `cardFromMetric` gives for the same choice: a classification gap should cost one row's
 * badges, never the analytics page.
 *
 * ⚠️ THAT FALLBACK NOW RETURNS THE SAME VALUE AS A DELIBERATELY-EMPTY CLASSIFICATION, and
 * a caller cannot tell them apart. It does not need to: both render no badge row, which is
 * the right answer for a primitive and the least-wrong one for an id that should not exist.
 * The distinction is enforced where it can actually be enforced — at COMPILE time, by
 * `ANALYTICS_METRIC_SOLUTIONS` being total over `AnalyticsMetricId`. Do not try to
 * reintroduce it here by returning `null` for the unknown case: that would put a
 * "should be impossible" branch into every call site to serve a state the type system
 * already rules out.
 */
export const analyticsMetricSolutions = (
  id: string,
): readonly AnalyticsSolutionCategory[] => {
  /*
    ⚠️ A VARIANT ID RESOLVES TO ITS METRIC (VERA-260), rather than falling through to the
    empty fallback above.

    The header says this function "takes the base id", which was a true description of
    every call site while the library listed metrics only. VERA-260 gave it a second kind
    of row — a PRESET, a variant card the `Display as` chips cannot reach
    (`analyticsIsLibraryPresetCardId`) — and that row's id IS a variant id. Left to the
    fallback it would render no badges at all, which is the "uncategorised metric" look the
    `scans_by_country` row already shipped once and which this file's own note about that
    incident describes.

    Resolving here rather than at the call site keeps the contract "hand me a card id" for
    every caller, instead of one that must remember which of two id spaces it holds. A
    solution is a property of the MEASUREMENT, and a display variant is the same
    measurement drawn differently, so the base id is the honest answer and not an
    approximation.
  */
  const parsed = parseAnalyticsVariantCardId(id)
  const metricId = parsed ? parsed.baseId : id
  return ANALYTICS_METRIC_SOLUTIONS[metricId as AnalyticsMetricId] ?? []
}
